import { useEffect, useRef } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'

import type { PaymentFormSnapshot } from '@/hooks/use-order-payment-tracking'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

function getPaymentMethods() {
    return [
        { value: 'cash', label: m.payment_method_cash() },
        { value: 'bank_transfer', label: m.payment_method_bank() },
    ]
}

interface Props {
    defaultAmount?: number
    /** What the order still owes; a larger amount is rejected before the API call. */
    maxAmount?: number
    /** Must report its own failure (e.g. mutation onError toast); a rejection is swallowed here. */
    onSubmit: (data: {
        amount: number
        method: string
        reference?: string
    }) => Promise<void>
    onCancel?: () => void
    isPending?: boolean
    /** Every edit, for analytics; the values must not leave the browser raw. */
    onValuesChange?: (values: PaymentFormSnapshot) => void
    /** Submit was blocked by validation (no API call). */
    onInvalid?: () => void
}

export function PaymentForm({
    defaultAmount = 0,
    maxAmount,
    onSubmit,
    onCancel,
    isPending,
    onValuesChange,
    onInvalid,
}: Props) {
    const schema = z.object({
        amount: z.coerce
            .number()
            .min(1, m.validation_quantity_positive())
            .max(
                maxAmount ?? Infinity,
                m.payment_amount_exceeds_remaining({
                    max: formatVND(maxAmount ?? 0),
                }),
            ),
        method: z.string().min(1),
        reference: z.string().optional(),
    })

    type FormData = z.infer<typeof schema>

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            amount: defaultAmount,
            method: 'cash',
            reference: '',
        },
    })

    const valuesChanged = useRef(onValuesChange)
    valuesChanged.current = onValuesChange
    useEffect(() => {
        const subscription = form.watch((values) =>
            valuesChanged.current?.({
                amount: values.amount,
                method: values.method ?? '',
                reference: values.reference,
            }),
        )
        return () => subscription.unsubscribe()
    }, [form])

    const handleSubmit = form.handleSubmit(async (data) => {
        // react-hook-form rethrows a rejected onSubmit, which surfaces as an
        // unhandled rejection. The caller's mutation already toasted the error;
        // keep the dialog open so the cashier can correct the amount.
        try {
            await onSubmit({
                amount: data.amount,
                method: data.method,
                reference: data.reference || undefined,
            })
        } catch {
            // reported by the caller
        }
    }, onInvalid)

    return (
        <FormProvider {...form}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <Field name="amount" label={m.payment_amount()} required>
                    <Input
                        id="amount"
                        type="number"
                        min={1}
                        // step=1, not 1000: a non-1 step makes the browser reject
                        // any amount that is not a multiple of it, blocking exact
                        // đồng entry (e.g. a 1270063 payment).
                        step={1}
                        {...form.register('amount')}
                    />
                </Field>

                <Field name="method" label={m.payment_method()} required>
                    <Controller
                        name="method"
                        control={form.control}
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onValueChange={field.onChange}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {getPaymentMethods().map((pm) => (
                                        <SelectItem
                                            key={pm.value}
                                            value={pm.value}
                                        >
                                            {pm.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    />
                </Field>

                <Field name="reference" label={m.payment_reference()}>
                    <Input
                        id="reference"
                        placeholder={m.payment_reference_placeholder()}
                        {...form.register('reference')}
                    />
                </Field>

                <div className="flex justify-end gap-2 pt-2">
                    {onCancel && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onCancel}
                        >
                            {m.common_cancel()}
                        </Button>
                    )}
                    <Button type="submit" disabled={isPending}>
                        {isPending
                            ? m.common_processing()
                            : m.payment_confirm()}
                    </Button>
                </div>
            </form>
        </FormProvider>
    )
}
